import {
  FaHome, FaBan, FaCog, FaTimes, FaWallet, FaToriiGate, FaNewspaper, FaTelegramPlane, FaHandHoldingUsd
} from 'react-icons/fa';
import { IoIosStats } from "react-icons/io";
import { IoShieldCheckmarkSharp } from "react-icons/io5";
import { TfiStatsUp } from "react-icons/tfi";
import { TbHttpPost } from "react-icons/tb";
import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const Sidebar = ({ isOpen, onClose, name, namee, mail, setting }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [activeTouchItem, setActiveTouchItem] = useState(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const isActive = (path) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  const handleTouchStart = (itemId) => setActiveTouchItem(itemId);
  const handleTouchEnd = () => setTimeout(() => setActiveTouchItem(null), 200);

  const navItems = [
    {
      group: 'Menu',
      items: [
        { path: '/adminBT', icon: FaHome, label: 'Dashboard', id: 'adminBT' },
        { path: '/adminBT/settings', icon: FaCog, label: 'General Settings', id: 'settings' }
      ]
    },
    {
      group: 'Report',
      items: [
        { path: '/adminBT/performance', icon: IoIosStats, label: 'Performance', id: 'performance' },
        { path: '/adminBT/offerWise', icon: FaNewspaper, label: 'Offer Wise', id: 'offerWise' },
        { path: '/adminBT/conversion', icon: TfiStatsUp, label: 'Conversions', id: 'conversion' }
      ]
    },
    {
      group: 'Payment',
      items: [
        { path: '/adminBT/gateway', icon: FaToriiGate, label: 'Gateway', id: 'gateway' }
      ]
    },
    {
      group: 'Security',
      items: [
        { path: '/adminBT/ban', icon: FaBan, label: 'Ban VPAs', id: 'ban' },
        { path: '/adminBT/whitelistIp', icon: IoShieldCheckmarkSharp, label: 'Whitelist Ip', id: 'whitelistIp' },
        { path: '/adminBT/postback', icon: TbHttpPost, label: 'Postback', id: 'postback' }
      ]
    }
  ];

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="sidebar-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <motion.aside
        ref={sidebarRef}
        className={`sidebar ${isOpen ? 'open' : ''}`}
        initial={{ x: '-100%' }}
        animate={{ x: isOpen ? 0 : '-100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <div className="sidebar-header">
          <div className="app-brand">
            <FaWallet className="wallet-icon" />
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {name || 'Votex Wallet'}
            </motion.span>
          </div>
          <motion.button
            className="close-button"
            onClick={onClose}
            aria-label="Close sidebar"
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaTimes />
          </motion.button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((group, index) => (
            <div key={index} className="nav-group">
              <motion.div
                className="nav-group-title"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 + 0.2 }}
              >
                {group.group}
              </motion.div>

              {group.items.map((item, itemIndex) => {
                const ItemComponent = item.isExternal ? 'a' : Link;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 + itemIndex * 0.03 + 0.2 }}
                  >
                    <ItemComponent
                      to={item.isExternal ? undefined : item.path}
                      href={item.isExternal ? item.path : undefined}
                      className={`nav-item ${isActive(item.path) ? 'active' : ''} ${activeTouchItem === item.id ? 'touch-active' : ''}`}
                      onTouchStart={() => handleTouchStart(item.id)}
                      onTouchEnd={handleTouchEnd}
                      {...(item.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      <item.icon className="nav-icon" />
                      <span>{item.label}</span>
                      <motion.div
                        className="active-indicator"
                        initial={{ scale: 0 }}
                        animate={{ scale: isActive(item.path) ? 1 : 0 }}
                      />
                    </ItemComponent>
                  </motion.div>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <motion.div
            className="user-profile"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="user-initial">
              {namee ? namee.split(' ').map(n => n[0]).join('') : 'JD'}
            </div>
            <div className="user-info">
              <span className="user-name">{namee || 'Admin'}</span>
              <span className="user-email">{mail}</span>
            </div>
          </motion.div>
        </div>
      </motion.aside>

      <style jsx global>{`
        :root {
          --primary: #8b5cf6;
          --primary-light: #c4b5fd;
          --primary-dark: #6d28d9;
          --dark: #0f172a;
          --darker: #020617;
          --light: #f8fafc;
          --lighter: #ffffff;
          --gray: #94a3b8;
          --glass: rgba(15, 23, 42, 0.8);
          --glass-border: rgba(255, 255, 255, 0.12);
        }

        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(5px);
          z-index: 90;
        }

        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 280px;
          background: var(--darker);
          border-right: 1px solid var(--glass-border);
          display: flex;
          flex-direction: column;
          z-index: 100;
          overflow-y: auto;
          box-shadow: 5px 0 30px rgba(0, 0, 0, 0.3);
        }

        .sidebar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid var(--glass-border);
        }

        .app-brand {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--light);
        }

        .wallet-icon {
          font-size: 1.5rem;
          color: var(--primary);
        }

        .close-button {
          background: none;
          border: none;
          color: var(--gray);
          font-size: 1.25rem;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 50%;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-button:hover {
          background: rgba(255, 255, 255, 0.1);
          color: var(--lighter);
        }

        .sidebar-nav {
          flex: 1;
          padding: 1rem 0;
          overflow-y: auto;
        }

        .nav-group {
          margin-bottom: 1.5rem;
        }

        .nav-group-title {
          padding: 0.5rem 1.5rem;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--gray);
          margin-bottom: 0.5rem;
        }

        .nav-item {
          display: flex;
          align-items: center;
          padding: 0.75rem 1.5rem;
          color: var(--light);
          text-decoration: none;
          position: relative;
          transition: all 0.3s ease;
          gap: 0.75rem;
        }

        .nav-item:hover {
          background: rgba(255, 255, 255, 0.05);
          color: var(--primary-light);
        }

        .nav-item.active {
          color: var(--primary);
          background: rgba(139, 92, 246, 0.1);
        }

        .nav-item.touch-active {
          background: rgba(255, 255, 255, 0.1);
        }

        .nav-icon {
          font-size: 1.1rem;
          flex-shrink: 0;
        }

        .active-indicator {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: var(--primary);
          border-radius: 0 4px 4px 0;
        }

        .sidebar-footer {
          padding: 1.5rem;
          border-top: 1px solid var(--glass-border);
        }

        .user-profile {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .user-initial {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--primary);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          flex-shrink: 0;
        }

        .user-info {
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .user-name {
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-email {
          font-size: 0.75rem;
          color: var(--gray);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        @media (max-width: 768px) {
          .sidebar {
            width: 260px;
          }
        }
      `}</style>
    </>
  );
};

export default Sidebar;

